import { useEffect, useState, useRef } from "react";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import ProductModal from "../components/ProductModal";
import Breadcrumbs from "../components/Breadcrumbs";
import { useCart } from "../context/CartContext";
import axios from "axios";
import useSearchedProducts from "../hooks/useSearchedProducts";

function Vip() {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [vipProducts, setVipProducts] = useState([]); // Original products
  const [filteredVipProducts, setFilteredVipProducts] = useState([]); // Filtered list
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
  const { selectedVips, addVip, removeVip } = useCart();

  // Ref for the product grid
  const gridRef = useRef(null);

  // Fetch VIP products
  useEffect(() => {
    const fetchVipProducts = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/products?category=vip`);
        setVipProducts(res.data);
        setFilteredVipProducts(res.data);
      } catch (err) {
        console.error("Error fetching VIP products:", err);
      }
    };
    fetchVipProducts();
  }, [BASE_URL]);

  // Search & filter using custom hook
  const { filteredProducts, isFiltered, resetProducts } = useSearchedProducts(filteredVipProducts);

  // Handle filter by gender
  const handleFilter = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);

    if (category === "All") {
      setFilteredVipProducts(vipProducts); // Reset filtered list
    } else {
      const filteredByCategory = vipProducts.filter(
        (p) => p.gender?.toLowerCase() === category.toLowerCase()
      );
      setFilteredVipProducts(filteredByCategory);
    }

    // ✅ Scroll to top of grid on filter
    if (gridRef.current) gridRef.current.scrollIntoView({ behavior: "smooth" });
  };

  // Toggle selection
  const handleToggleSelection = (product) => {
    const exists = selectedVips.find((item) => item._id === product._id);
    exists ? removeVip(product._id) : addVip(product);
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const currentProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const vipUnavailable = filteredProducts.length === 0;

  // Scroll to top of grid on page change
  const handlePageChange = (pageNum) => {
    setCurrentPage(pageNum);
    if (gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Reset search
  const handleResetProducts = () => {
    resetProducts();
    setFilteredVipProducts(vipProducts);
    setSelectedCategory("All");
    setCurrentPage(1);

    // ✅ Scroll to top of grid on reset
    if (gridRef.current) gridRef.current.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div>
      {/* Breadcrumbs */}
      <div className="px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs current="VIP Gift Collection" />
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gray-100 py-20 text-center px-4 sm:px-6" data-aos="fade-down">
        <h1 className="text-4xl font-bold mb-4">VIP Gift Collection</h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Explore our curated collection of accessories from premium brands.
          These gifts are designed to impress.
        </p>
      </section>

      {/* Categories */}
      {!isFiltered && (
        <div className="flex flex-wrap justify-center gap-3 mt-16">
          {["All", "Male", "Female", "Unisex"].map((label, idx) => (
            <button
              key={idx}
              onClick={() => handleFilter(label)}
              className={`px-4 py-2 border rounded-full text-sm transition ${
                selectedCategory === label
                  ? "bg-blue-600 text-white"
                  : "hover:bg-blue-600 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Show All Button when filtered via search */}
      {isFiltered && (
        <div className="flex justify-center mb-6">
          <button
            onClick={handleResetProducts}
            className="mt-10 px-4 py-2 text-sm font-medium text-blue-600 border border-blue-400 rounded-full hover:bg-blue-600 hover:text-white transition"
          >
            Show All VIP Collections
          </button>
        </div>
      )}

      {/* VIP Content */}
      <section
        ref={gridRef} // Attach ref here
        className="px-4 sm:px-6 pt-10 pb-20 max-w-6xl mx-auto"
        data-aos="fade-up"
      >
        {vipUnavailable ? (
          <div className="flex flex-col items-center justify-center text-center bg-gray-50 border rounded-xl py-20 px-6">
            <h2 className="text-2xl font-semibold mb-3">VIP Collection Coming Soon</h2>
            <p className="text-gray-600 max-w-md mb-6">
              We’re curating an exclusive range of premium designer accessories for
              our VIP gift collection. These products will be available very soon.
            </p>
            <span className="inline-block bg-yellow-100 text-yellow-700 text-sm px-4 py-2 rounded-full">
              Launching shortly
            </span>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 min-h-[400px]"> {/* ✅ min-height prevents double scroll */}
              {currentProducts.map((product, i) => (
                <div key={product._id} className="w-full">
                  <ProductCard
                    product={product}
                    index={i}
                    onClick={() => handleToggleSelection(product)}
                    selected={selectedVips.some((item) => item._id === product._id)}
                    onView={() => setSelectedProduct(product)}
                  />
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-wrap justify-center mt-6 gap-2">
                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      className={`px-3 py-1 border rounded ${
                        pageNum === currentPage ? "bg-blue-600 text-white" : ""
                      }`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Product Modal */}
      <ProductModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleToggleSelection}
        selectedItems={selectedVips}
      />

      {/* Footer */}
      <div data-aos="fade-up">
        <Footer />
      </div>
    </div>
  );
}

export default Vip;
